import { authorizeBaseUrl, InlineButton } from "@coherehealth/common";
import { UserContext } from "UserContext";
import {
  PaddedAlert,
  fetchedAlertState,
  loadingAlertState,
  timedOutAlertState,
  useClinicalReviewStyles,
} from "../caseCompletePageStyles";
import { useContext, useEffect, useState } from "react";
import { getMostRecentSRWithNonTerminalStatus } from "util/serviceRequestUtil";
import { ServiceCase, useGetServiceCase, useGetServiceRequestCases, useStartWorkingCase } from "@coherehealth/qm-api";
import { useParams } from "react-router-dom";
import { FetchContinuationCaseAlertState } from "../caseCompletePageUtils";

export const ContinueToClinicalReviewBanner = () => {
  const { caseId } = useParams();
  const clincalReviewClasses = useClinicalReviewStyles();
  const { queues, setActiveQueue } = useContext(UserContext);
  const [fetchedServiceCase, setFetchedServiceCase] = useState<ServiceCase>();
  const [fetchContinuationCaseState, setFetchContinuationCaseState] =
    useState<FetchContinuationCaseAlertState>(loadingAlertState);
  const { data: serviceCase, refetch: refetchServiceCase } = useGetServiceCase({ id: caseId ? caseId : "" });
  const mostRecentNonTerminalSr = getMostRecentSRWithNonTerminalStatus(serviceCase?.outcome?.serviceRequests);
  const isFaxIntakeCase = serviceCase?.caseType === "FAX_INTAKE";
  const canContinueToClinicalReview =
    serviceCase?.healthPlan === "GEISINGER" &&
    isFaxIntakeCase &&
    (serviceCase.outcome?.outcome === "NEW_SR" || serviceCase.outcome?.outcome === "FAX_ATTACHED");

  const { data: cases, refetch: getServiceRequestCases } = useGetServiceRequestCases({
    id: mostRecentNonTerminalSr?.serviceRequestId || "",
    queryParams: {
      caseType: "RN_REVIEW",
    },
    lazy: true,
  });
  const { mutate: startWorkingContinuationCase } = useStartWorkingCase({ id: cases?.at(0)?.id || "" });
  useEffect(() => {
    if (mostRecentNonTerminalSr?.serviceRequestId) {
      getServiceRequestCases();
    }
  }, [mostRecentNonTerminalSr, getServiceRequestCases]);
  useEffect(() => {
    const interval = setInterval(async () => {
      if (canContinueToClinicalReview && fetchContinuationCaseState.state === "LOADING") {
        const srCases = await getServiceRequestCases();
        if (srCases?.length === 1) {
          setFetchedServiceCase(srCases?.at(0));
          setFetchContinuationCaseState(fetchedAlertState);
        } else {
          const serviceCase = await refetchServiceCase();
          if (serviceCase && !fetchedServiceCase) {
            setFetchedServiceCase(serviceCase);
            setFetchContinuationCaseState(fetchedAlertState);
          }
        }
      }
    }, 2000);
    setTimeout(() => {
      clearInterval(interval);
      fetchContinuationCaseState.state !== "FETCHED" && !mostRecentNonTerminalSr?.serviceRequestId
        ? setFetchContinuationCaseState(timedOutAlertState)
        : setFetchContinuationCaseState(fetchedAlertState);
    }, 30000);
    return () => {
      clearInterval(interval);
    };
  }, [
    canContinueToClinicalReview,
    fetchContinuationCaseState.state,
    fetchedServiceCase,
    getServiceRequestCases,
    mostRecentNonTerminalSr?.serviceRequestId,
    refetchServiceCase,
  ]);
  const getClickEvent = async () => {
    const caseParams = fetchedServiceCase?.outcome?.serviceRequests
      ? getMostRecentSRWithNonTerminalStatus(fetchedServiceCase?.outcome?.serviceRequests)?.serviceRequestId
      : fetchedServiceCase?.id;
    if (!fetchedServiceCase?.outcome?.serviceRequests) {
      const continuationCase = await startWorkingContinuationCase({ id: caseParams });
      await setActiveQueue(queues.filter((queue) => queue.name === "GHP - Clinical Review")[0].id || "");
      window.location.assign(
        `${authorizeBaseUrl()}/${
          continuationCase?.serviceRequest?.serviceRequestId
        }/review?createNewNurseReview&caseId=${caseParams}`
      );
    } else {
      window.location.assign(`${authorizeBaseUrl()}/${caseParams}/review?createNewNurseReview`);
    }
  };

  const showSeverityBasedAuthStatus =
    serviceCase === null || serviceCase?.outcome?.serviceRequests?.[0]?.status === "APPROVED" ? undefined : "warning";

  return serviceCase !== null ? (
    <PaddedAlert
      className={clincalReviewClasses.readyForClinicalReview}
      message={
        serviceCase?.outcome?.serviceRequests?.[0]?.status === "APPROVED"
          ? "This request has been auto-approved."
          : "This request is ready for clinical review."
      }
      severity={showSeverityBasedAuthStatus}
      action={
        serviceCase?.outcome?.serviceRequests?.[0]?.status !== "APPROVED" && (
          <InlineButton
            disabled={fetchContinuationCaseState.state === "LOADING"}
            startIcon={fetchContinuationCaseState.startIcon}
            onClick={fetchContinuationCaseState.state === "LOADING" ? () => {} : getClickEvent}
          >
            {fetchContinuationCaseState.buttonBody}
          </InlineButton>
        )
      }
    />
  ) : null;
};
